import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { Percent, Undo2, Printer } from "lucide-react";

const fmt = (n) => `₡${(Number(n) || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function printReceipt({ title, debt, lines, footerNote, user }) {
  const body = `<!doctype html>
<html><head><meta charset="utf-8"><title>${title} — ${debt.customer_name || ""}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  body { font-family: 'Courier New', monospace; font-size: 12px; margin: 0; color: #000; }
  h2 { text-align: center; margin: 4px 0; font-size: 14px; }
  .meta div { display: flex; justify-content: space-between; font-size: 11px; }
  hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 11px; }
  .total { font-weight: bold; border-top: 1px solid #000; padding-top: 4px; }
  .sig { margin-top: 22px; text-align: center; border-top: 1px dashed #000; padding-top: 4px; font-size: 10px; }
  .foot { text-align: center; font-size: 10px; margin-top: 10px; color: #444; }
</style></head><body>
  <h2>${title}</h2>
  <div class="meta">
    <div><span>Cliente:</span><span>${(debt.customer_name || "").slice(0, 26)}</span></div>
    <div><span>Fecha:</span><span>${new Date().toLocaleString("es-CR")}</span></div>
    <div><span>Cajero:</span><span>${user?.username || ""}</span></div>
    <div><span>#Deuda:</span><span>${debt.id.slice(0, 8).toUpperCase()}</span></div>
  </div>
  <hr>
  ${lines.map(l => `<div class="row"><span>${l.k}</span><span>${l.v}</span></div>`).join("")}
  <hr>
  <div class="foot">${footerNote || ""}</div>
  <div class="sig">FIRMA RESPONSABLE</div>
  <script>window.onload = () => { window.print(); setTimeout(()=>window.close(), 400); };</script>
</body></html>`;
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) { toast.error("El navegador bloqueó la impresión. Habilitá popups."); return; }
  w.document.open(); w.document.write(body); w.document.close();
}

export function DebtDiscountDialog({ open, onOpenChange, debt, user, onSaved }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) { setAmount(""); setReason(""); setPassword(""); } }, [open]);
  if (!debt) return null;

  const current = Number(debt.amount_colones || 0);
  const numAmt = Number(amount || 0);
  const newAmount = Math.max(0, current - numAmt);

  const apply = async () => {
    if (numAmt <= 0) return toast.error("Monto inválido");
    if (numAmt > current) return toast.error(`Descuento no puede ser mayor a ${fmt(current)}`);
    if (reason.trim().length < 3) return toast.error("Motivo requerido");
    if (!password) return toast.error("Clave MASTER requerida");
    setSaving(true);
    try {
      const r = await axiosInstance.post(`/debts/${debt.id}/apply-discount`, {
        password, amount_colones: numAmt, reason,
      });
      toast.success(r.data.message || "Descuento aplicado");
      printReceipt({
        title: "NOTA DE DESCUENTO", debt, user,
        lines: [
          { k: "Deuda anterior", v: fmt(current) },
          { k: "Descuento", v: `-${fmt(numAmt)}` },
          { k: "Motivo", v: reason },
          { k: "Deuda nueva", v: fmt(r.data.new_debt_amount) },
        ],
        footerNote: `Autorizado por MASTER ${user?.username || ""}`,
      });
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al aplicar descuento"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="debt-discount-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Percent className="w-5 h-5 text-purple-600" /> Aplicar Descuento</DialogTitle>
          <DialogDescription>Rebaja la deuda pendiente y registra un movimiento tipo &ldquo;descuento&rdquo; en reportes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm bg-slate-50 border rounded px-3 py-2">
            <div className="flex justify-between"><span>Cliente:</span><b>{debt.customer_name}</b></div>
            <div className="flex justify-between"><span>Deuda actual:</span><b>{fmt(current)}</b></div>
          </div>
          <div>
            <Label>Monto a descontar (₡)</Label>
            <Input type="number" min="0" step="1" value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/^0+(?=\d)/, ""))}
              data-testid="discount-amount" />
          </div>
          <div>
            <Label>Motivo <span className="text-rose-600">*</span></Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="Ej: promoción cierre, ajuste comercial…"
              data-testid="discount-reason" />
          </div>
          <div>
            <Label>Clave MASTER</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="4981" data-testid="discount-password" />
          </div>
          <div className="text-xs bg-purple-50 border border-purple-200 rounded px-2 py-1 flex justify-between">
            <span>Nueva deuda:</span><b className="text-purple-800">{fmt(newAmount)}</b>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={apply} disabled={saving || numAmt <= 0 || !reason.trim() || !password}
            className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="discount-apply-btn">
            <Printer className="w-4 h-4 mr-2" /> {saving ? "Aplicando…" : "Aplicar e imprimir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function DebtReturnDialog({ open, onOpenChange, debt, user, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState({}); // {product_id: qty}
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!open || !debt) return;
    setSelection({}); setReason(""); setPassword("");
    (async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get(`/debts/${debt.id}/returnable-items`);
        setItems(r.data.items || []);
      } catch (e) {
        toast.error(getErrorMessage(e, "No se pudieron cargar los artículos"));
        setItems([]);
      } finally { setLoading(false); }
    })();
  }, [open, debt]);

  if (!debt) return null;

  const setQty = (rowKey, val) => setSelection({ ...selection, [rowKey]: val });
  const totalSubtotal = items.reduce((acc, it) => {
    const key = `${it.sale_id}:${it.product_id}`;
    const q = Number(selection[key] || 0);
    return acc + Math.min(q, it.quantity_available) * it.subtotal_unit;
  }, 0);

  const apply = async () => {
    const payload = items
      .map((it) => {
        const key = `${it.sale_id}:${it.product_id}`;
        const q = Math.min(Number(selection[key] || 0), it.quantity_available);
        return q > 0 ? { sale_id: it.sale_id, product_id: it.product_id, quantity: q } : null;
      })
      .filter(Boolean);
    if (!payload.length) return toast.error("Marcá al menos un artículo");
    if (reason.trim().length < 3) return toast.error("Motivo requerido");
    if (!password) return toast.error("Clave MASTER requerida");
    setSaving(true);
    try {
      const r = await axiosInstance.post(`/debts/${debt.id}/return-items`, {
        password, reason, items: payload,
      });
      toast.success(r.data.message || "Devolución aplicada");
      const lines = [
        ...r.data.items.map(it => ({ k: `${it.code || ""} ${it.name || ""}  x${it.quantity}`, v: fmt(it.subtotal) })),
        { k: "Total devuelto", v: fmt(r.data.returned_subtotal) },
        { k: "Motivo", v: reason },
        { k: "Deuda nueva", v: fmt(r.data.new_debt_amount) },
      ];
      printReceipt({ title: "NOTA DE DEVOLUCIÓN", debt, lines, user, footerNote: `Autorizado por MASTER ${user?.username || ""}` });
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al aplicar devolución"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="debt-return-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 className="w-5 h-5 text-amber-600" /> Devolución desde Cuenta por Cobrar</DialogTitle>
          <DialogDescription>Marcá los artículos a devolver. Se restaura el inventario y se rebaja la deuda (no reintegra efectivo).</DialogDescription>
        </DialogHeader>
        <div className="text-sm bg-slate-50 border rounded px-3 py-2 mb-3">
          <div className="flex justify-between"><span>Cliente:</span><b>{debt.customer_name}</b></div>
          <div className="flex justify-between"><span>Deuda actual:</span><b>{fmt(debt.amount_colones || 0)}</b></div>
        </div>
        {loading ? (
          <p className="text-center text-slate-500 py-6">Cargando artículos…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-500 py-6">Sin artículos disponibles para devolver</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-100 text-slate-700">
                <th className="text-left p-2 border">Código</th>
                <th className="text-left p-2 border">Producto</th>
                <th className="text-right p-2 border">P.Unit</th>
                <th className="text-center p-2 border">Disp.</th>
                <th className="text-center p-2 border w-24">Devolver</th>
                <th className="text-right p-2 border">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const key = `${it.sale_id}:${it.product_id}`;
                const q = Number(selection[key] || 0);
                const subt = Math.min(q, it.quantity_available) * it.subtotal_unit;
                return (
                  <tr key={key} className="hover:bg-slate-50">
                    <td className="p-2 border font-mono text-xs">{it.code || "-"}</td>
                    <td className="p-2 border">{it.name}<div className="text-[10px] text-slate-400">Venta {it.sale_short}</div></td>
                    <td className="p-2 border text-right font-mono">{fmt(it.subtotal_unit)}</td>
                    <td className="p-2 border text-center">{it.quantity_available}</td>
                    <td className="p-1 border">
                      <Input type="number" min="0" max={it.quantity_available} step="1"
                        value={selection[key] || ""}
                        onChange={(e) => setQty(key, e.target.value.replace(/^0+(?=\d)/, ""))}
                        className="h-8 text-center"
                        data-testid={`return-qty-${it.product_id}`} />
                    </td>
                    <td className="p-2 border text-right font-mono">{fmt(subt)}</td>
                  </tr>
                );
              })}
              <tr className="bg-amber-50 font-bold">
                <td className="p-2 border" colSpan={5}>TOTAL A REBAJAR</td>
                <td className="p-2 border text-right text-amber-800 text-base" data-testid="return-total">{fmt(totalSubtotal)}</td>
              </tr>
            </tbody>
          </table>
        )}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <Label>Motivo <span className="text-rose-600">*</span></Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="Ej: producto defectuoso, cambio…"
              data-testid="return-reason" />
          </div>
          <div>
            <Label>Clave MASTER</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="4981" data-testid="return-password" />
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={apply} disabled={saving || totalSubtotal <= 0 || !reason.trim() || !password}
            className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="return-apply-btn">
            <Printer className="w-4 h-4 mr-2" /> {saving ? "Aplicando…" : "Aplicar e imprimir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
